import { fmtDate } from '../../utils/datetime';
import React, { useEffect, useState } from "react";
import { createNewDoctorCategory, deleteDoctorCategory, editDoctorCategory, getAllDoctorCategories } from "../../api/doctor";
import { useAuth } from "../../context/AuthContext";
import { Pencil, Trash2, User } from "lucide-react";
import { toast } from '../../utils/toast';
import { Modal } from "./utils/Modal";
import { useFormik } from "formik";
import * as Yup from "yup";
import { DoctorCategory } from "./utils/interface";

const DoctorCategories: React.FC = () => {
    const [categories, setCategories] = useState<DoctorCategory[]>([]);
    const { user } = useAuth();

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentCategoryId, setCurrentCategoryId] = useState(0);

    useEffect(() => {
        if (user && user.tenant_id) {
            fetchDoctorCategories();
        }
    }, [user]);

    /** Fetch Categories */
    const fetchDoctorCategories = async () => {
        try {
            const response = await getAllDoctorCategories(user.tenant_id);
            setCategories(response);
        } catch (error) {
            console.error("Error fetching doctor categories:", error);
        }
    };

    /** Formik */
    const initialValues = { name: "" };

    const categorySchema = Yup.object({
        name: Yup.string().required("Please enter category name"),
    });

    const {
        values,
        setValues,
        handleBlur,
        handleChange,
        handleSubmit,
        errors,
        touched,
    } = useFormik({
        initialValues,
        validationSchema: categorySchema,
        onSubmit: async (values) => {
            if (!isEdit) {
                try {
                    const response = await createNewDoctorCategory(
                        user.tenant_id,
                        values.name
                    );

                    if (response.status === 200) {
                        setIsAddModalOpen(false);
                        fetchDoctorCategories();
                        setValues({ name: "" });
                        toast.success("Doctor Category Created Successfully!");
                    }
                } catch (error: any) {
                    toast.error(error.message || "Something went wrong.");
                }
            }

            if (isEdit) {
                try {
                    const response = await editDoctorCategory(
                        currentCategoryId,
                        user.tenant_id,
                        values.name
                    );

                    if (response.status === 200) {
                        setIsEditModalOpen(false);
                        setIsEdit(false);
                        setCurrentCategoryId(0);
                        setValues({ name: "" });
                        fetchDoctorCategories();
                        toast.success("Doctor Category Updated Successfully!");
                    }
                } catch (error: any) {
                    toast.error(error.message || "Something went wrong.");
                }
            }
        },
    });

    /** Edit (future-ready) */
    const handleEdit = (item: DoctorCategory) => {
        setValues({ name: item.name });
        setCurrentCategoryId(item.id);
        setIsEdit(true);
        setIsEditModalOpen(true);
    };

    /** Delete */
    const handleDelete = async (id: number) => {
        if (!await toast.confirm("This action cannot be undone.", "Delete category?", "Delete")) return;
        try {
            await deleteDoctorCategory(id);
            setCategories(categories.filter((c) => c.id !== id));
            toast.success("Category deleted successfully.");
        } catch (error: any) {
            toast.error(error.message || "Failed to delete category.");
        }
    };

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                    Doctor Categories  ({categories.length})
                </h2>
                <button
                    onClick={() => {
                        setIsEdit(false);
                        setValues({ name: "" });
                        setIsAddModalOpen(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg"
                >
                    + Add Category
                </button>
            </div>

            {/* List */}
            <div className="bg-white rounded-xl shadow-md">
                <div className="p-6">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-200">
                                    <th className="text-left py-3 px-4">SN</th>
                                    <th className="text-left py-3 px-4">Name</th>
                                    <th className="text-left py-3 px-4">Updated At</th>
                                    <th className="text-left py-3 px-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {categories.map((item, index) => (
                                    <tr
                                        key={item.id}
                                        className="border-b border-gray-100 hover:bg-blue-50"
                                    >
                                        <td className="py-4 px-4">{index + 1}</td>
                                        <td className="py-4 px-4">{item.name}</td>
                                        <td className="py-4 px-4">
                                            {fmtDate(item.updated_at)}
                                        </td>
                                        <td className="py-4 px-4">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleEdit(item)}
                                                    className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                                    title="Edit"
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </button>

                                                <button
                                                    onClick={() => handleDelete(item.id)}
                                                    className="p-2 bg-red-600 text-white rounded hover:bg-red-700"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {categories.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={4}
                                            className="py-6 text-center text-gray-500"
                                        >
                                            No categories found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Add Modal */}
            <Modal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                title="Add New Doctor Category"
            >
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Name *
                        </label>
                        <input
                            name="name"
                            value={values.name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        {errors.name && touched.name && (
                            <p className="text-red-500">{errors.name}</p>
                        )}
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={() => setIsAddModalOpen(false)}
                            className="px-4 py-2 border border-gray-300 rounded-lg"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Add Category
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Edit Modal (future) */}
            <Modal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                title="Edit Doctor Category"
            >
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Name *
                        </label>
                        <input
                            name="name"
                            value={values.name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={() => setIsEditModalOpen(false)}
                            className="px-4 py-2 border border-gray-300 rounded-lg"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg"
                        >
                            Update
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default DoctorCategories;
